import React, { useState, useEffect, useCallback } from 'react';
import { Box, LinearProgress, IconButton, Button, Dialog, DialogTitle, DialogContent, Paper, Typography, CircularProgress } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { DataGridPro, GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { GridColDef, GridToolbarFilterButton, GridToolbarContainer, 
    getGridStringOperators, GridToolbarColumnsButton, GridToolbarDensitySelector
} from '@mui/x-data-grid';
import { RiExpandUpDownLine } from "react-icons/ri";
import { FaArchive, FaDownload, FaPlus, FaRegEye, FaTrash } from 'react-icons/fa';
import { DashboardButton } from '../components/Elements/DashboardTile';
import Swal from 'sweetalert2';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { writeXlsxFile } from '../helper/utils';
import { AddStampedDocument } from '../components/AddEdit/AddStampedDocument';
import moment from 'moment';
import { LightBlueBtnColor, DarkBlueBtnColor, HoverUnselectedLightBlueBtnColor, WhiteColor, GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../helper/theme';

export interface StampedDocumentRow {
    id: number;    // stampedDocumentDetailId
    type: string;  // documentType
    client: string;
    partnersId: number;
    sentOn: string;
    sentBy: string;
};
export interface StampedDocument {
    stampedDocumentId: number;
    documentName: string;
    blobAddress: string;
    fileSizeKB: string;
    sentOn: string;
};
export interface DocumentDetail {
    stampedDocumentDetailId: number;
    documentType: string;
    userFirstName: string;
    client: string; 
    partnersId: number;
    documents: StampedDocument[];
    isArchived: boolean;
};
export interface StampedDocumentApiResponse {
    totalCount: number;
    currentPage: number;
    totalPages: number;
    documentDetails: DocumentDetail[];
};


const ClientUserRoles: string[] = [
    "Customer User",
    "Client Admin",
    "Client Processors",
    "State Reporter",
];

export enum TabNames {
    SentDocuments = 'Sent Documents',
    Archive = 'Archive',
};

export const SendStampedDocument: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;
    const IsUserClient = Boolean(ClientUserRoles.some(role => user?.userRoles?.includes(role)));
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [addStampedDocumentOpen, setAddStampedDocumentOpen] = useState(false);
    const [viewAttachmentsOpen, setViewAttachmentsOpen] = useState(false);
    const [selectedDocumentDetail, setSelectedDocumentDetail] = useState<DocumentDetail | null>(null);
    const [documentDetailsData, setDocumentDetailsData] = useState<DocumentDetail[]>([]);
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(null);
    const [documentViewerFileName, setDocumentViewerFileName] = useState<string | null>(null);
    const [documentViewerBlobAddress, setDocumentViewerBlobAddress] = useState<string | null>(null);
    const [documentLoading, setDocumentLoading] = useState(false);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<string>('desc');
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    // tab state
    const [selectedTab, setSelectedTab] = useState<TabNames>(TabNames.SentDocuments);
    const handleTabChange = (tab: TabNames) => {
        if (tab !== selectedTab) {
            setSelectedTab(tab);
            setCurrentPage(0); // Reset to first page when tab changes
        }
    };

    const filterOperators = getGridStringOperators();

    const [rows, setRows] = useState<StampedDocumentRow[]>([]);

    const columns: GridColDef[] = [
        { field: 'type', headerName: 'Type', 
            type: 'string', 
            flex: 1, minWidth: 200, 
            align: 'center', headerAlign: 'center', 
            hideSortIcons: true, filterOperators, 
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
        },
        { field: 'client', headerName: 'Client', 
            type: 'string', 
            flex: 1, minWidth: 150, 
            align: 'center', headerAlign: 'center', 
            hideSortIcons: true, filterOperators, 
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
        },
        { field: 'sentOn', headerName: 'Sent On', 
            type: 'date',
            flex: 0.5, minWidth: 120, 
            align: 'center', headerAlign: 'center', 
            hideSortIcons: true, filterOperators, 
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
            valueGetter: (params) => {
                if (!params.value) return null;
                return moment.utc(params.value).toDate();
            },
            valueFormatter: (params) => {
                if (!params.value) return '';
                return moment.utc(params.value).format('MM/DD/YYYY');
            },
        },
        { field: 'sentBy', headerName: 'Sent By', 
            type: 'string', 
            flex: 0.5, minWidth: 75, 
            align: 'center', headerAlign: 'center', 
            hideSortIcons: true, filterOperators, 
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
        },
        { field: 'action', headerName: 'Action', 
            flex: 0.5, minWidth: 100, 
            align: 'center', headerAlign: 'center', 
            filterable: false, 
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            hideSortIcons: true,
            renderHeader: (params) => (
                <div style={{ paddingLeft: '80px' }}>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            renderCell: (params) => (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '100%',
                    gap: '8px',
                    paddingLeft: '80px'
                }}>
                    <IconButton title="View" 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewAttachments(params.row.id);
                        }}
                        sx={{ color: '#1976D2' }}
                    ><FaRegEye /></IconButton>
                    <IconButton title="Download"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadRow(params.row.id);
                        }}
                        sx={{ color: '#1976D2' }}
                    ><FaDownload /></IconButton>
                </div>
            )
        }
    ];

    const handleNewSubmission = () => {
        setAddStampedDocumentOpen(true);
    };

    const handleCloseAddStampedDocument = () => {
        setAddStampedDocumentOpen(false);
    };

    const handleStampedDocumentSuccess = () => {
        fetchStampedDocuments();
    };

    const handleViewAttachments = (rowId: number) => {
        const documentDetail = documentDetailsData.find(detail => detail.stampedDocumentDetailId === rowId);
        if (documentDetail) {
            setSelectedDocumentDetail(documentDetail);
            setViewAttachmentsOpen(true);
        }
    };

    const handleCloseViewAttachments = () => {
        setViewAttachmentsOpen(false);
        setSelectedDocumentDetail(null);
    };

    const handleViewDocument = async (blobAddress: string, fileName?: string) => {
        if (!blobAddress) return;

        // Get file extension to determine how to display
        const extension = (fileName || blobAddress).split('.').pop()?.toLowerCase();
        const isPdf = extension === 'pdf';

        setDocumentLoading(true);
        setDocumentViewerOpen(true);
        setDocumentViewerFileName(fileName || "document.pdf");
        setDocumentViewerBlobAddress(blobAddress);

        try {
            if (isPdf) {

                const response = await fetch(blobAddress, {
                    method: "GET",
                    mode: "cors",
                    credentials: "omit",
                    cache: "no-cache",
                });

                if (!response.ok) {
                    throw new Error(
                        `Failed to download file: ${response.status} ${response.statusText}`,
                    );
                }

                const downloadedBlob = await response.blob();
                const cleanBlob = new Blob([downloadedBlob], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(cleanBlob);
                
                setDocumentViewerUrl(blobUrl);
                setDocumentLoading(false);
            } else {
                // For Office files (DOC, DOCX, XLS, XLSX): Use Microsoft Office Online Viewer
                const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(blobAddress)}`;
                
                setDocumentViewerUrl(officeViewerUrl);
                setDocumentLoading(false);
            }
        } catch (error) {
            console.error("Error loading document:", error);
            setDocumentLoading(false);
            setDocumentViewerOpen(false);

            Swal.fire({
                title: "Error",
                text: "Failed to load file. Opening in new tab instead.",
                icon: "warning",
                confirmButtonText: "OK",
            }).then(() => {
                window.open(blobAddress, "_blank");
            });
        }
    };

    const handleCloseDocumentViewer = () => {
        if (documentViewerUrl && documentViewerUrl.startsWith('blob:')) {
            URL.revokeObjectURL(documentViewerUrl);
        }
        setDocumentViewerOpen(false);
        setDocumentViewerUrl(null);
        setDocumentViewerFileName(null);
        setDocumentViewerBlobAddress(null);
    };

    const handleDownloadFromViewer = () => {
        if (documentViewerBlobAddress && documentViewerFileName) {
            handleDownloadDocument(documentViewerBlobAddress, documentViewerFileName);
        }
    };

    const handleDownloadDocument = async (blobAddress: string, documentName: string) => {
        try {
            const response = await fetch(blobAddress);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = documentName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading document:', error);
            // Fallback to opening in new tab
            window.open(blobAddress, '_blank');
        }
    };

    const handleDownloadRow = async (rowId: number) => {
        const documentDetail = documentDetailsData.find(detail => detail.stampedDocumentDetailId === rowId);
        if (!documentDetail || !documentDetail.documents || documentDetail.documents.length === 0) {
            Swal.fire({
                title: 'No Documents',
                text: 'No documents found for this record.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }

        // If only one document, download directly
        if (documentDetail.documents.length === 1) {
            const doc = documentDetail.documents[0];
            if (doc.blobAddress) {
                window.open(doc.blobAddress, '_blank');
            }
            return;
        }

        // If multiple documents, generate a zip file
        setLoading(true);
        try {
            const documentsPayload = documentDetail.documents
                .filter((doc) => doc.documentName && doc.blobAddress)
                .map((doc) => ({
                    documentName: doc.documentName || '',
                    blobAddress: doc.blobAddress || '',
                }));

            if (documentsPayload.length === 0) {
                Swal.fire({
                    title: 'Error',
                    text: 'No valid documents to download.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const response = await apiClient('/StampedDocument/generateStampedDocumentsZip', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }),
                body: JSON.stringify({
                    documents: documentsPayload,
                }),
            });

            if (response?.status && response?.data) {
                if (response.data.zipUrl) {
                    window.open(response.data.zipUrl, '_blank');
                } else if (response.data.zipBase64) {
                    const byteCharacters = atob(response.data.zipBase64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/zip' });

                    const fileName = `StampedDocuments_${rowId}_${moment().format('YYYY-MM-DD')}.zip`;
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } else {
                    Swal.fire({
                        title: 'Warning',
                        text: response?.message || 'Download initiated, but no file data received.',
                        icon: 'warning',
                        confirmButtonText: 'OK',
                    });
                    return;
                }

                Swal.fire({
                    title: 'Success!',
                    text: response?.message || 'Documents downloaded successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
            } else {
                throw new Error(response?.message || 'Failed to generate ZIP file.');
            }
        } catch (error: any) {
            console.error('Error downloading documents:', error);
            Swal.fire({
                title: 'Error',
                text: error?.message || 'Failed to download documents. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
        }
    };


    const handleFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
        setFilterModel(newFilterModel);
        setCurrentPage(0); // Reset to first page when filters change
    }, []);

    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        if (newSortModel.length > 0) {
            setSortField(newSortModel[0].field);
            setSortDirection(newSortModel[0].sort || 'asc');
        } else {
            setSortField('');
            setSortDirection('desc');
        }
        setCurrentPage(0); // Reset to first page when sort changes
    }, []);

    const fetchStampedDocuments = async () => {
        setLoading(true);
        try {
            const requestBody = {
                pageNumber: currentPage + 1, // API uses 1-based indexing
                pageSize: pageSize,
                field: sortField || 'sentOn',
                sort: sortDirection || 'desc',
                filterModel: filterModel
            };

            const response = await apiClient('/StampedDocument/getStampedDocuments', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }),
                body: JSON.stringify(requestBody)
            });

            if (response?.data) {
                const data: StampedDocumentApiResponse = response.data;
                const documentDetails = data.documentDetails || (data as any).documentDetails || [];
                
                // Filter by archive status based on selected tab
                const filteredDocumentDetails = documentDetails.filter((detail: DocumentDetail) => {
                    const isArchived = detail.isArchived || false;
                    if (selectedTab === TabNames.SentDocuments) {
                        return !isArchived; // Show non-archived documents
                    } else {
                        return isArchived; // Show archived documents
                    }
                });
                
                const mappedRows: StampedDocumentRow[] = filteredDocumentDetails
                    .filter(detail => detail && detail.stampedDocumentDetailId && typeof detail.stampedDocumentDetailId === 'number')
                    .map((detail: DocumentDetail) => {
                        // Get the sentOn date from the first document, or use the latest one
                        let sentOnDate = '';
                        if (detail.documents && detail.documents.length > 0) {
                            const sortedDocs = [...detail.documents].sort((a, b) => 
                                new Date(b.sentOn).getTime() - new Date(a.sentOn).getTime()
                            );
                            sentOnDate = sortedDocs[0].sentOn 
                                ? moment.utc(sortedDocs[0].sentOn).format('MM/DD/YYYY') 
                                : '';
                        }
                        
                        return {
                            id: detail.stampedDocumentDetailId,
                            type: detail.documentType || '',
                            client: detail.client || '',
                            partnersId: detail.partnersId || 0,
                            sentOn: sentOnDate,
                            sentBy: detail.userFirstName || '',
                            isArchived: detail.isArchived || false,
                        };
                    })
                    .filter(row => row && row.id && typeof row.id === 'number');
                const validRows = mappedRows.filter(row => 
                    row && 
                    row.id !== undefined && 
                    row.id !== null && 
                    typeof row.id === 'number' &&
                    row.id > 0
                );
                
                setRows(validRows);
                setTotalCount(validRows.length);
                setDocumentDetailsData(documentDetails);
            } else {
                setRows([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching stamped documents:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch stamped documents. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStampedDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, sortField, sortDirection, filterModel, selectedTab]);

    const handleDelete = async() => {
        if (selectedRows.length === 0) {
            Swal.fire({
                title: 'No items selected',
                text: "Please select at least one item to delete",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#02A4E3',
                cancelButtonColor: '#d33',
                confirmButtonText: 'OK',
            });
        } else {
            Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#02A4E3',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Delete',
                customClass: {
                    confirmButton: 'btn-radius',
                    cancelButton: 'btn-radius',
                    popup: 'card-radius'
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    setLoading(true);
                    // Send stampedDocumentDetailId from selected rows
                    const requestBody = {
                        stampedDocumentDetailId: selectedRows.filter(id => id && typeof id === 'number' && id > 0)
                    };
                    const response = await apiClient('/StampedDocument/deleteStampedDocuments', {
                        method: 'POST',
                        headers: new Headers({
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        }),
                        body: JSON.stringify(requestBody)
                    });
                    
                    if (response?.status === true || response?.data) {
                        // Clear selected rows and refresh the data after successful deletion
                        setSelectedRows([]);
                        await fetchStampedDocuments();
                        
                        Swal.fire({
                            title: 'Deleted successfully',
                            text: response?.message || "The items have been deleted successfully",
                            icon: 'success',
                            confirmButtonColor: '#02A4E3',
                            confirmButtonText: 'OK',
                        });
                    } else {
                        Swal.fire({
                            title: 'Error',
                            text: response?.message || 'Failed to delete stamped documents. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                        });
                    }
                    setLoading(false);
                }
            });
        }
    };

    const markAsArchived = () => {
        // only if all selected rows are unarchived
        if(selectedRows.length > 0 && selectedRows.every(row => documentDetailsData.find(detail => detail.stampedDocumentDetailId === row)?.isArchived === false)) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => documentDetailsData.find(detail => detail.stampedDocumentDetailId === row)?.stampedDocumentDetailId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to move these documents to archive?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to move to archive
                    apiClient(`/StampedDocument/markAsArchived?stampedDocumentDetailIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the stamped documents after moving to archive
                        fetchStampedDocuments();
                        Swal.fire({
                            title: 'Documents marked as archived',
                            text: 'The selected documents have been marked as archived',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error marking documents as archived:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No documents selected</div>',
                html: '<div style="color: white;">Please select at least one document to mark as archived</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };
    const markAsUnarchived = () => {
        // only if all selected rows are archived
        if(selectedRows.length > 0 && selectedRows.every(row => documentDetailsData.find(detail => detail.stampedDocumentDetailId === row)?.isArchived === true)) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => documentDetailsData.find(detail => detail.stampedDocumentDetailId === row)?.stampedDocumentDetailId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to move these documents from archive?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to move from archive
                    apiClient(`/StampedDocument/markAsUnarchived?stampedDocumentDetailIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the stamped documents after moving from archive
                        fetchStampedDocuments();
                        Swal.fire({
                            title: 'Documents moved from archive',
                            text: 'The selected documents have been moved from archive',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error moving documents from archive:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No documents selected</div>',
                html: '<div style="color: white;">Please select at least one archived document to move from archive</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };


    const handleExport = () => {
        const rowsToExport = selectedRows.length > 0 
            ? rows.filter(row => selectedRows.includes(row.id))
            : rows;

        if (rowsToExport.length === 0) {
            Swal.fire({
                title: 'No data to export',
                text: selectedRows.length > 0 
                    ? 'No selected rows to export.'
                    : 'There are no stamped documents to export.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        exportToExcel(rowsToExport);
    };

    const exportToExcel = (rowsToExport: StampedDocumentRow[] = rows) => {
        const headers = ['Type', 'Client', 'Sent On', 'Sent By'];
        
        const dataRows = rowsToExport.map(row => [
            row.type || '',
            row.client || '',
            row.sentOn || '',
            row.sentBy || '',
        ]);
        const exportData = [headers, ...dataRows];
        const timeStamp = moment().format('YYYY.M.D');
        const rowCount = rowsToExport.length;
        const filename = `StampedDocuments_${timeStamp}${selectedRows.length > 0 ? `_${rowCount}Selected` : ''}.xlsx`;

        writeXlsxFile(exportData, filename);
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarFilterButton className="me-3 sky-blue" />
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" />
            </GridToolbarContainer>
        );
    }

    function EditToolbar() {
        return (
            <div className="p-2 d-flex justify-content-end datagrid-butons">
                <div className="ml-2"><CustomToolbar /></div>
            </div>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="card-title">
                    <span className="d-flex">I Want To... Send a Stamped Document</span>
                </div>
            </div>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>
            <Box sx={{ width: "100%" }}>
                <Box sx={{ width: 'auto', display: 'flex', gap: 1, mb: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                    {!IsUserClient && (<>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{ minWidth: '150px', transform: 'scale(0.95)', transformOrigin: 'left center' }}>
                            <Box onClick={handleNewSubmission}
                                sx={{
                                    backgroundColor: '#0d4a63',
                                    borderRadius: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    minHeight: '51px',
                                    minWidth: '50px',
                                    padding: '5px 7px',
                                    '&:hover': {
                                        backgroundColor: '#0a3a4f',
                                        transform: 'translateY(-4px)',
                                    },
                                }}
                            >
                                <Box sx={{
                                    color: 'white',
                                    fontSize: '22px',
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    fontFamily: "'Proxima Nova', sans-serif",
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <Box component="span" sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}><FaPlus /></Box>
                                    <span style={{ marginLeft: '10px' }}>New Submission</span>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ minWidth: '150px', transform: 'scale(0.95)', transformOrigin: 'left center' }}>
                            <DashboardButton title="Delete" onClick={handleDelete} 
                                icon={<FaTrash />} 
                                variant="secondary"
                                padding="5px 21px"
                                disabled={selectedRows.length === 0}
                            />
                        </Box>
                        <Box sx={{ minWidth: '150px', transform: 'scale(0.95)', transformOrigin: 'left center' }}>
                            <DashboardButton title={selectedTab === TabNames.Archive ? 'Unarchive' : 'Archive'} 
                                onClick={selectedTab === TabNames.Archive ? markAsUnarchived : markAsArchived} 
                                icon={<FaArchive />} 
                                variant="primary"
                                padding="5px 21px"
                                disabled={selectedRows.length === 0}
                            />
                        </Box>
                    </Box>
                    
                    {/* Tabs Section */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 5, }}>
                        <div className="text-center">
                            <Button onClick={() => handleTabChange(TabNames.SentDocuments)}
                                variant={selectedTab === TabNames.SentDocuments ? 'contained' : 'outlined'}
                                sx={{
                                    borderRadius: '25px',
                                    padding: '10px 40px',
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    backgroundColor: selectedTab === TabNames.SentDocuments ? LightBlueBtnColor : 'transparent',
                                    color: selectedTab === TabNames.SentDocuments ? WhiteColor : LightBlueBtnColor,
                                    borderColor: LightBlueBtnColor,
                                    '&:hover': {
                                        backgroundColor: selectedTab === TabNames.SentDocuments ? DarkBlueBtnColor : HoverUnselectedLightBlueBtnColor,
                                        borderColor: LightBlueBtnColor,
                                    },
                                }}
                            >Sent Documents</Button>
                        </div>
                        <div className="text-center">
                            <Button onClick={() => handleTabChange(TabNames.Archive)}
                                variant={selectedTab === TabNames.Archive ? 'contained' : 'outlined'}
                                sx={{
                                    borderRadius: '25px',
                                    padding: '10px 40px',
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    backgroundColor: selectedTab === TabNames.Archive ? LightBlueBtnColor : 'transparent',
                                    color: selectedTab === TabNames.Archive ? WhiteColor : LightBlueBtnColor,
                                    borderColor: LightBlueBtnColor,
                                    '&:hover': {
                                        backgroundColor: selectedTab === TabNames.Archive ? DarkBlueBtnColor : HoverUnselectedLightBlueBtnColor,
                                        borderColor: LightBlueBtnColor,
                                    },
                                }}
                            >Archived</Button>
                        </div>
                    </Box>
                    </>)}
                    
                    <Box sx={{ minWidth: '150px' }}>
                        <Button onClick={handleExport}
                            className="active-btn-blue"
                            sx={{
                                borderRadius: '100px',
                                textTransform: 'none',
                                padding: '10px 20px',
                            }}
                        >Export</Button>
                    </Box>
                </Box>
                <Box overflow={"scroll"}>
                    <Box sx={{ p: 3 }}>
                        <div style={{ height: "59vh", display: "flex", flexDirection: "column", width: "100%" }}>
                            <div style={{ flex: 1, overflow: "auto" }}>
                                <DataGridPro
                                    rows={rows}
                                    columns={columns}
                                    page={currentPage}
                                    pageSize={pageSize}
                                    pagination
                                    rowCount={totalCount}
                                    paginationMode="server"
                                    sortingMode="server"
                                    filterMode="server"
                                    filterModel={filterModel}
                                    onFilterModelChange={handleFilterModelChange}
                                    onSortModelChange={handleSortModelChange}
                                    getRowId={(row) => row.id}
                                    onPageChange={(newPage) => setCurrentPage(newPage)}
                                    onPageSizeChange={(newPageSize) => {
                                        setPageSize(newPageSize);
                                        setCurrentPage(0);
                                    }}
                                    rowsPerPageOptions={[25, 50, 100]}
                                    checkboxSelection={true}
                                    onSelectionModelChange={(newSelectionModel) => {
                                        setSelectedRows(newSelectionModel as number[]);
                                    }}
                                    components={{
                                        Toolbar: EditToolbar,
                                    }}
                                    sx={{
                                        "& .MuiDataGrid-columnHeaderTitle": {
                                            whiteSpace: "normal",
                                            lineHeight: "normal",
                                        },
                                        fontFamily: "'Proxima Nova', sans-serif",
                                        border: "0px",
                                        fontSize: "16px",
                                    }}
                                />
                            </div>
                        </div>
                    </Box>
                </Box>
            </Box>
            <AddStampedDocument 
                Open={addStampedDocumentOpen} 
                handleClose={handleCloseAddStampedDocument}
                onSuccess={handleStampedDocumentSuccess}
                onLoadingChange={setLoading}
            />
            <Dialog
                open={viewAttachmentsOpen}
                onClose={handleCloseViewAttachments}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogContent sx={{ backgroundColor: '#ffffff', padding: '16px', margin: 0 }}>
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            fontWeight: 'bold', 
                            marginBottom: '16px',
                            fontFamily: "'Proxima Nova', sans-serif",
                        }}
                    >
                        Document Attachments:
                    </Typography>
                    {selectedDocumentDetail && selectedDocumentDetail.documents && selectedDocumentDetail.documents.length > 0 ? (
                        (() => {
                            const documentRows = selectedDocumentDetail.documents.map((doc, index) => ({
                                id: doc.stampedDocumentId || index,
                                type: selectedDocumentDetail.documentType || 'N/A',
                                documentName: doc.documentName || 'N/A',
                                blobAddress: doc.blobAddress || '',
                            }));

                            const columns: GridColDef[] = [
                                {
                                    field: 'download',
                                    headerName: 'Download',
                                    width: 180,
                                    align: 'center',
                                    headerAlign: 'center',
                                    sortable: false,
                                    filterable: false,
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 3,
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewDocument(params.row.blobAddress, params.row.documentName)}
                                                disabled={!params.row.blobAddress}
                                                title="View"
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDownloadDocument(params.row.blobAddress, params.row.documentName)}
                                                disabled={!params.row.blobAddress}
                                                title="Download"
                                            >
                                                <CloudDownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ),
                                },
                                {
                                    field: 'type',
                                    headerName: 'Type',
                                    width: 250,
                                    align: 'center',
                                    headerAlign: 'center',
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            {params.value}
                                        </Box>
                                    ),
                                },
                                {
                                    field: 'documentName',
                                    headerName: 'Document Name',
                                    flex: 1,
                                    minWidth: 300,
                                    align: 'center',
                                    headerAlign: 'center',
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            {params.value}
                                        </Box>
                                    ),
                                },
                            ];

                            return (
                                <Paper
                                    sx={{
                                        width: '100%',
                                        border: 'none',
                                        overflow: 'hidden',
                                        boxShadow: 'none',
                                        borderRadius: 0,
                                        margin: 0,
                                        padding: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: Math.max(400, 56 + documentRows.length * 52 + 56),
                                            width: '100%',
                                            margin: 0,
                                            padding: 0,
                                        }}
                                    >
                                        <DataGridPro
                                            rows={documentRows}
                                            columns={columns}
                                            pagination
                                            pageSize={5}
                                            rowsPerPageOptions={[5, 10, 25]}
                                            disableColumnMenu
                                            disableSelectionOnClick
                                            getRowHeight={() => 52}
                                            sx={{
                                                border: 'none',
                                                '& .MuiDataGrid-cell': {
                                                    borderBottom: '1px solid #e0e0e0',
                                                    borderRight: 'none',
                                                    padding: '12px 24px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                },
                                                '& .MuiDataGrid-columnHeaders': {
                                                    backgroundColor: '#1976D2',
                                                    color: '#ffffff',
                                                    fontWeight: 'bold',
                                                    borderBottom: '2px solid #1565c0',
                                                    minHeight: '56px !important',
                                                    maxHeight: '56px !important',
                                                },
                                                '& .MuiDataGrid-columnHeaderTitle': {
                                                    color: '#ffffff',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px',
                                                },
                                                '& .MuiDataGrid-columnHeader': {
                                                    borderRight: 'none',
                                                    padding: '12px 24px',
                                                    minHeight: '56px !important',
                                                    maxHeight: '56px !important',
                                                },
                                                '& .MuiDataGrid-columnSeparator': {
                                                    display: 'none',
                                                },
                                                '& .MuiDataGrid-row': {
                                                    backgroundColor: '#ffffff',
                                                },
                                                '& .MuiDataGrid-row:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Paper>
                            );
                        })()
                    ) : (
                        <Typography sx={{ 
                            color: '#333', 
                            textAlign: 'center',
                            fontFamily: "'Proxima Nova', sans-serif",
                            padding: '20px'
                        }}>
                            No attachments found
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* Document Viewer Dialog */}
            <Dialog
                open={documentViewerOpen}
                onClose={handleCloseDocumentViewer}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    style: {
                        maxHeight: "90vh",
                    },
                }}
            >
                <DialogTitle className="card-title">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                fontWeight: "medium",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                paddingRight: "16px",
                            }}
                        >
                            {documentViewerFileName || ""}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                                onClick={handleDownloadFromViewer}
                                sx={{
                                    color: "#1976D2",
                                    "&:hover": {
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                    },
                                }}
                                aria-label="download"
                                title="Download"
                            >
                                <CloudDownloadIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleCloseDocumentViewer}
                                sx={{
                                    color: "#666",
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                        color: "#000",
                                    },
                                }}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{ padding: 0, height: "80vh", position: "relative" }}
                >
                    {documentLoading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : documentViewerUrl ? (
                        <Box sx={{ width: "100%", height: "100%" }}>
                            <iframe
                                src={documentViewerUrl}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                }}
                                title="Document Viewer"
                            />
                        </Box>
                    ) : null}
                </DialogContent>
            </Dialog>
        </LocalizationProvider>
    );
};

export default SendStampedDocument;
